import React, { useState, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { ButtonProps } from '@ygtechnologies/rubus-ui';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { ValidationScope } from '@/components/forms/validation';
import { useAsyncState } from '@/utils/hooks';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import ExternalAgreementCreateWizardStepMain, { ExternalAgreementMainInfo } from './ExternalAgreementCreateWizardStepMain';
import ExternalAgreementCreateWizardStepPrice from './ExternalAgreementCreateWizardStepPrice';
import ExternalAgreementCreateWizardStepParticipants, {
    ParticipantStepData,
} from './ExternalAgreementCreateWizardStepParticipants';
import { getProjectFeeSets } from '@/services/fees';
import { LoadingIndicator, SimpleWizard, SimpleWizardStep } from '@ygtechnologies/bygr-frontend-component-library';
import { ExternalAgreementLineCreateOptions, createExternalAgreement } from '@/services/external-agreements';
import { NoAvailableFeesAlert } from '@/seller/components/fees';

enum Step {
    Main,
    Price,
    Participants,
}
const Steps = [Step.Main, Step.Price, Step.Participants];

interface ExternalAgreementWizardProps {
    residentialUnitId: number;
    onSaved(): void;
    onCancel(): void;
    onBack?(): void;
}

function getEmptyDetails(): ExternalAgreementMainInfo {
    return {
        documentType: null,
        files: [],
        name: '',
    };
}

function getEmptyParticipantStepData(): ParticipantStepData {
    return {
        participants: [],
        changeRequestDescription: '',
    };
}

const ExternalAgreementCreateWizard: React.FC<ExternalAgreementWizardProps> = ({ residentialUnitId, onCancel, onSaved }) => {
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);
    const profileId = useSelector((state: IState) => state.global.activeProfile!.id);
    const [step, setStep] = useState(Step.Main);

    const [projectFeeSets] = useAsyncState(() => getProjectFeeSets(projectId), [projectId], null);

    const [mainInfo, setMainInfo] = useState<ExternalAgreementMainInfo>(getEmptyDetails());
    const [lines, setLines] = useState<ExternalAgreementLineCreateOptions[]>([]);
    const [participantStepData, setParticipantStepData] = useState<ParticipantStepData>(getEmptyParticipantStepData());

    const [saving, setSaving] = useState(false);

    const saveButtonProps: Partial<ButtonProps> = useMemo(
        () => ({
            children: <Trans>Save</Trans>,
            loading: saving,
        }),
        [saving]
    );

    const handleSubmit = async () => {
        setSaving(true);

        try {
            await createExternalAgreement({
                profileId,
                residentialUnitId,
                name: mainInfo!.name,
                documentTypeId: mainInfo.documentType!.id,
                files: mainInfo!.files,
                lines: lines,
                participants: participantStepData.participants.map((x) => ({ ...x, priority: 0 })),
                participantsNote:
                    participantStepData.participants.length > 1 ? participantStepData.changeRequestDescription : '',
                // tags: mainInfo.tags,
                // changeRequestDescription:
                // participantStepData.participants.length > 1 ? participantStepData.changeRequestDescription : null,
                // overrideBuyerOptionGroupIds: selectedBuyerOptionGroupIds,
            });

            triggerHandler(onSaved);
        } finally {
            setSaving(false);
        }
    };

    if (!projectFeeSets) {
        return <LoadingIndicator />;
    }

    if (projectFeeSets.length === 0) {
        return <NoAvailableFeesAlert />;
    }

    return (
        <ValidationScope>
            <SimpleWizard
                steps={Steps}
                currentStep={step}
                onStepChange={(step) => setStep(step)}
                onSubmit={handleSubmit}
                onCancel={onCancel}
                saveButtonProps={saveButtonProps}>
                <SimpleWizardStep stepNumber={Step.Main} header={<Trans>Basic info</Trans>}>
                    <ExternalAgreementCreateWizardStepMain
                        value={mainInfo}
                        profileId={profileId}
                        onChange={(newValue) => setMainInfo(newValue)}
                    />
                </SimpleWizardStep>
                <SimpleWizardStep stepNumber={Step.Price} header={<Trans>Pricing</Trans>}>
                    <ExternalAgreementCreateWizardStepPrice
                        value={lines}
                        onChange={(newValue) => setLines(newValue)}
                        projectFeeSets={projectFeeSets}
                    />
                </SimpleWizardStep>

                <SimpleWizardStep stepNumber={Step.Participants} header={<Trans>Additional participants</Trans>}>
                    <ExternalAgreementCreateWizardStepParticipants
                        projectId={projectId}
                        value={participantStepData}
                        onChange={setParticipantStepData}
                    />
                </SimpleWizardStep>
            </SimpleWizard>
        </ValidationScope>
    );
};

export default ExternalAgreementCreateWizard;